// ═══ 대행사 신청 확인 — 거래처 묶음 (W1a ④) ═══
// 머리(거래처 이름·대수·이번에 추가한 '새 거래처') + 그 묶음 차 카드(children) + 수거 요청일 때 묶음 수거 주소 선택:
//   본인 묶음 = [대행사 + 회사 수거 주소] · [다른 주소 입력]
//   하위 묶음 = [대행사에서 모아서 + 회사 수거 주소] · [거래처 저장 주소](있을 때만) · [다른 주소 입력 → 입력칸]
// value = { kind: 'collect'|'saved'|'custom', address } — 저장 주소는 고를 때 값을 복사한다. 입력 상한 500 은 onChange 에서 자른다.
// '다른 주소 입력' 칸: 비면 칸 아래 안내(주소 필수 §5) · [주소 검색](canSearch = Daum Postcode 있음) → 채우고 커서 끝.
// props: { t, client, count, isNew, accountName, collectAddr, intakeMethod, canSearch, value, onChange(value), children }
import React, { useCallback, useRef, useState } from 'react';
import { T, R, S, FONT } from '../../theme';
import { card as cardBase, input as inputSt, warnSt } from '../../simple/screens/request/bits';
import AddrPickModal from '../../simple/screens/request/AddrPickModal';
import { clientName } from './UnitPickSheet';

var box = Object.assign({}, cardBase, { padding: 14, gap: 10 });
var head = { display: 'flex', alignItems: 'center', gap: 8 };
var name = { flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: T.ink };
var pill = { flex: 'none', fontSize: FONT.badge, fontWeight: 700, padding: '3px 8px', borderRadius: R.pill };
var cnt = Object.assign({}, pill, { background: T.accentTint, color: T.accentPress });
var fresh = Object.assign({}, pill, { background: T.track, color: T.dim });
var sec = { fontSize: FONT.label, fontWeight: 700, color: T.muted, marginTop: 4 };
var opt = { display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', borderRadius: R.lg, border: '1.5px solid ' + T.bd, background: T.card };
var optLab = { display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' };
var an = { display: 'block', fontSize: 13.5, fontWeight: 700, color: T.ink };
var ad = { display: 'block', fontSize: 12, color: T.dim, marginTop: 2 };
var customRow = { display: 'flex', gap: 8 };
var searchBtn = Object.assign({}, S.btn, { height: 38, flex: 'none' });

function BrokerGroup(props) {
  var t = props.t;
  var c = props.client;
  var v = props.value || { kind: 'collect', address: '' };
  var saved = String(c.pickupAddress || '').trim();
  var customRef = useRef(null);
  var stPick = useState(false); var picking = stPick[0], setPicking = stPick[1];
  // 고정 콜백 — 호출측 onChange 는 렌더마다 새 함수라, 그대로 deps 에 넣으면 열린 검색창이 다시 임베드된다(AddrPickModal effect).
  var changeRef = useRef(props.onChange);
  changeRef.current = props.onChange;
  var onAddrPick = useCallback(function (addr) {
    setPicking(false);
    if (!addr) return;
    var a = addr.slice(0, 500);
    changeRef.current({ kind: 'custom', address: a });
    setTimeout(function () { var el = customRef.current; if (el) { el.focus(); el.setSelectionRange(a.length, a.length); } }, 0);
  }, [setPicking]);
  var choose = function (kind) {
    props.onChange({ kind: kind, address: kind === 'saved' ? saved : '' });
  };
  var option = function (kind, title, sub) {
    var on = v.kind === kind;
    // 고른 줄도 같은 중립 모양 — 표시는 라디오만(서류 전달 방식·수거 주소 선택에 바탕 칠 없음, Jin 11/05)
    return (<div key={kind} style={opt} data-pick={kind} data-on={on ? '1' : '0'}>
      <label style={optLab}>
        <input type="radio" name={'pickup-' + c.id} checked={on} onChange={function () { choose(kind); }} />
        <span><b style={an}>{title}</b>{sub ? <span style={ad}>{sub}</span> : null}</span>
      </label>
      {on && kind === 'custom' ? (<div style={customRow}>
        <input ref={customRef} style={Object.assign({}, inputSt, { flex: 1, minWidth: 0 })} value={v.address} data-testid={'pickup-custom-' + c.id}
          aria-label={t('malsoModeBrokerPickupCustom')}
          onChange={function (e) { props.onChange({ kind: 'custom', address: e.target.value.slice(0, 500) }); }} />
        {props.canSearch ? (<button type="button" style={searchBtn} data-testid={'pickup-search-' + c.id}
          onClick={function () { setPicking(true); }}>{t('simplePickAddr')}</button>) : null}
      </div>) : null}
      {on && kind === 'custom' && !String(v.address).trim()
        ? <span style={warnSt} data-testid={'pickup-custom-hint-' + c.id}>{t('malsoModeBrokerPickupCustomNeeded')}</span> : null}
    </div>);
  };
  return (<div style={box} data-testid={'broker-group-' + c.id}>
    <div style={head}>
      <b style={name}>{clientName(t, c)}</b>
      <span style={cnt}>{t('malsoModeBrokerCarCount', { count: props.count })}</span>
      {props.isNew ? <span style={fresh} data-testid={'broker-new-' + c.id}>{t('malsoModeBrokerNewBadge')}</span> : null}
    </div>
    {props.children}
    {props.intakeMethod === '수거' ? (<React.Fragment>
      <span style={sec}>{t('carCancelPickupSection')}</span>
      {c.isSelf ? option('collect', props.accountName, props.collectAddr)
        : option('collect', t('malsoModeBrokerPickupCollect', { broker: props.accountName }), props.collectAddr)}
      {!c.isSelf && saved ? option('saved', c.name, t('malsoModeBrokerPickupSavedTag', { address: saved })) : null}
      {option('custom', t('malsoModeBrokerPickupCustom'), '')}
      <AddrPickModal t={t} open={picking} onPick={onAddrPick} onClose={function () { setPicking(false); }} />
    </React.Fragment>) : null}
  </div>);
}

export default BrokerGroup;
